import { useState, useEffect, useMemo } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useAdminOverride } from "../hooks/useAdminOverride";
import { useGraphQL } from "../hooks/useGraphQL";

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  avatar_url: string;
  is_deleted_on_tw: boolean;
}

interface PeopleData {
  people: Person[];
}

const PEOPLE_QUERY = `
  query AdminGetPeople {
    people {
      id
      first_name
      last_name
      email_address
      avatar_url
      is_deleted_on_tw
    }
  }
`;

export function AdminUserOverride() {
  const { user } = useAuthContext();
  const { overrideEmail, setOverrideEmail } = useAdminOverride();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const peopleQuery = useGraphQL<PeopleData>();

  // Fetch people on mount
  useEffect(() => {
    peopleQuery.execute(PEOPLE_QUERY);
  }, []);

  // Filter and sort people
  const filteredPeople = useMemo(() => {
    if (!peopleQuery.data?.people) return [];

    return peopleQuery.data.people
      .filter((p) => {
        // Only show active mavenmm.com users
        if (p.is_deleted_on_tw) return false;
        if (!p.email_address?.endsWith("@mavenmm.com")) return false;

        // Apply search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
          return fullName.includes(query) || p.email_address.toLowerCase().includes(query);
        }
        return true;
      })
      .sort((a, b) => a.first_name.localeCompare(b.first_name));
  }, [peopleQuery.data?.people, searchQuery]);

  // Find currently selected person
  const selectedPerson = useMemo(() => {
    if (!overrideEmail || !peopleQuery.data?.people) return null;
    return peopleQuery.data.people.find(
      (p) => p.email_address.toLowerCase() === overrideEmail.toLowerCase()
    );
  }, [overrideEmail, peopleQuery.data?.people]);

  const displayName = selectedPerson
    ? `${selectedPerson.first_name}`
    : user?.email?.split("@")[0] || "self";

  const avatarUrl = selectedPerson?.avatar_url || undefined;

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-l border border-white/30 text-white hover:bg-white/10"
        >
          <span className="text-white/60">Viewing As</span>
          {avatarUrl && (
            <img src={avatarUrl} alt="" className="w-6 h-6 rounded-full" />
          )}
          <span className={overrideEmail ? "text-yellow-300" : "text-white"}>
            {displayName}
          </span>
        </button>
        {overrideEmail && (
          <button
            onClick={() => {
              setOverrideEmail(null);
              window.location.reload();
            }}
            className="text-sm px-2 py-1.5 rounded-r border border-l-0 border-white/30 text-white/50 hover:text-white hover:bg-white/10"
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-maven-teal-500"
                autoFocus
              />
            </div>

            {/* People list */}
            <div className="flex-1 overflow-y-auto">
              {peopleQuery.isLoading ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Loading...
                </div>
              ) : filteredPeople.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No results
                </div>
              ) : (
                filteredPeople.map((person) => (
                  <button
                    key={person.id}
                    onClick={() => {
                      setOverrideEmail(person.email_address);
                      setShowDropdown(false);
                      setSearchQuery("");
                      // Reload to refetch all data with new override
                      window.location.reload();
                    }}
                    className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-100 text-left ${
                      overrideEmail?.toLowerCase() === person.email_address.toLowerCase()
                        ? "bg-maven-teal-50"
                        : ""
                    }`}
                  >
                    {person.avatar_url ? (
                      <img
                        src={person.avatar_url}
                        alt=""
                        className="w-6 h-6 rounded-full"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200" />
                    )}
                    <span className="text-sm text-gray-900">
                      {person.first_name} {person.last_name}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
